import React, {useRef, useState, useEffect} from 'react'
import {
  UseFormRegister,
  FieldValues,
  Path,
  FieldErrors,
  RegisterOptions,
  UseFormSetValue,
} from 'react-hook-form'
import {IApplicationFormData} from '../../application/models/application-form'
import {getFieldError} from './getFieldError'

type OTPInputProps<T extends FieldValues> = {
  name: Path<T>
  length?: number
  numericOnly?: boolean
  disabled?: boolean
  errors?: FieldErrors<T>
  required?: boolean | string
  className?: string
  onChange?: (value: string) => void
  options?: RegisterOptions<T, Path<T>>
}

export const OTPInput = <T extends FieldValues>({
  name,
  length = 6,
  numericOnly = true,
  disabled = false,
  errors,
  required = false,
  className = '',
  onChange,
  options = {},
}: OTPInputProps<T>) => {
  const error = getFieldError(errors, name)
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''))
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])

  const isValidChar = (char: string) => {
    if (numericOnly) return /^[0-9]$/.test(char)
    return /^[a-zA-Z0-9]$/.test(char)
  }

  useEffect(() => {
    inputsRef.current[0]?.focus()
  }, [])

  const focusInput = (index: number) => {
    if (index >= 0 && index < length) {
      inputsRef.current[index]?.focus()
      inputsRef.current[index]?.select()
    }
  }

  const updateOtp = (idx: number, val: string) => {
    const newOtp = [...otp]
    newOtp[idx] = val
    setOtp(newOtp)

    const fullValue = newOtp.join('')
    if (onChange) onChange(fullValue)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const val = e.target.value

    if (val.length > 1) {
      const firstChar = val[0]
      if (!isValidChar(firstChar)) return
      updateOtp(idx, firstChar)
      focusInput(idx + 1)
      return
    }

    if (val === '') {
      updateOtp(idx, '')
      return
    }

    if (!isValidChar(val)) return

    updateOtp(idx, val)
    focusInput(idx + 1)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'Backspace') {
      e.preventDefault()

      if (otp[idx]) {
        updateOtp(idx, '')
      } else {
        focusInput(idx - 1)
        updateOtp(idx - 1, '')
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      focusInput(idx - 1)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      focusInput(idx + 1)
    }
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select()
  }

  return (
    <div>
      <div className={`d-flex gap-2 ${className}`}>
        {Array.from({length}).map((_, idx) => (
          <input
            key={idx}
            type='text'
            inputMode={numericOnly ? 'numeric' : 'text'}
            maxLength={1}
            className={`form-control text-center ${error ? 'is-invalid-otp' : ''}`}
            value={otp[idx]}
            disabled={disabled}
            onChange={(e) => handleChange(e, idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            ref={(el) => {
              inputsRef.current[idx] = el
            }}
            autoComplete='one-time-code'
            spellCheck={false}
            placeholder=' '
            onFocus={handleFocus}
          />
        ))}
      </div>

      {error && <div className='text-danger mt-1'>{error.message}</div>}
    </div>
  )
}
